import { useCallback, useEffect, useRef, useState } from 'react'
import API from '../../../../../services/API'

export function useIncidentLogs(incidentID, intervalMs = 2000) {
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [skip, setSkip] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const versionRef = useRef(null)

  const fetchLogs = useCallback(
    async (skipVal = 0, append = false) => {
      if (!incidentID) return

      try {
        setLoading(true)
        const res = await API.get(
          `/commandcontrol/incidents/${incidentID}/logs?skip=${skipVal}&limit=50`,
        )

        if (append) {
          setLogs((prev) => [...prev, ...res.data])
        } else {
          setLogs(res.data)
          setSkip(0)
        }

        setHasMore(res.data.length === 50)
      } catch (e) {
        console.error('Error fetching logs', e)
      } finally {
        setLoading(false)
      }
    },
    [incidentID],
  )

  useEffect(() => {
    if (!incidentID) return
    let cancelled = false

    const fetchVersion = async () => {
      try {
        const res = await API.get(
          `/commandcontrol/incidents/${incidentID}/logsVersion`,
        )
        if (cancelled) return

        const { logsVersion } = res.data
        if (versionRef.current !== logsVersion) {
          // version changed → reset logs, skip, and fetch fresh first page
          await fetchLogs(0, false)
          if (cancelled) return
          versionRef.current = logsVersion
        }
      } catch (e) {
        console.error('Error checking logsVersion', e)
      }
    }

    // initial fetch
    fetchLogs(0, false)

    // poll version
    const id = setInterval(fetchVersion, intervalMs)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [incidentID, intervalMs, fetchLogs])

  // load older logs
  const loadMore = async () => {
    if (loading || !hasMore) return

    const newSkip = skip + 50
    setSkip(newSkip)
    await fetchLogs(newSkip, true)
  }

  return { logs, loading, hasMore, loadMore }
}
